import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useQuery } from '@tanstack/react-query'

import { createCrmApiClient } from './CrmApi'
import { createRatingApiClient } from './RatingApi'
import { isOrganisationPrioritized } from './utils'
import Filter from './components/Filter'
import SortInfo from './components/SortInfo'
import OrganisationsList from './components/OrganisationsList'
import useScrolledToElement from '../hooks/useScrolledToElement'

const OrganisationComponentRoot = ({ businessArea, children, ...props }) => {
  const [scrollEnabled, setScrollEnabled] = useState(false)
  const scrolledToElement = useScrolledToElement({ enabled: scrollEnabled })

  const apiClient = createCrmApiClient({ apiUrl: props.crmApiUrl, apiToken: props.crmApiToken })
  const ratingApiClient = createRatingApiClient({ apiUrl: props.ratingApiUrl })

  const initialFilter = props.filter || {}

  const limit = initialFilter.limit ? parseInt(initialFilter.limit, 10) : 0
  const catalogEnabledOnly = initialFilter.catalogEnabledOnly

  const [selectedOrganisation, setSelectedOrganisation] = useState()
  const [countryCode, setCountryCode] = useState(initialFilter.countryCode)
  const [targetGroup, setTargetGroup] = useState(initialFilter.targetGroup)

  // in utils see hint on start-date/end-date -
  const isPrioritized = (organisation) => isOrganisationPrioritized({
    organisation,
    filterCountryCode: countryCode,
    filterContext: props.context
  })

  const {
    isLoading,
    isError,
    error,
    data: organisations
  } = useQuery(
    ['organisations'],
    () => apiClient.fetchOrganisations({ businessArea })
  )

  const ratingSummariesQuery = useQuery(
    ['rating_summaries'],
    () => ratingApiClient.fetchRatingSummaries({ businessArea })
  )

  if (isLoading || ratingSummariesQuery.isLoading) {
    return (
      <div className="d-flex flex-column align-items-center">
        <p>Loading...</p>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  if (isError || ratingSummariesQuery.isLoading) {
    return (
      <span className='text-danger'>
        {error}{ratingSummariesQuery.error}
      </span>
    )
  }

  // Collect all unique country codes from all organisations
  const countrySet = new Set()
  organisations.forEach((organisation) => {
    organisation.target_countries.forEach((country) => {
      countrySet.add(country)
    })
  })

  const countries = Array.from(countrySet)

  const targetGroupSet = new Set()
  organisations.forEach((organisation) => {
    organisation.target_groups.forEach((group) => {
      targetGroupSet.add(group)
    })
  })

  const targetGroups = Array.from(targetGroupSet)

  let filteredOrganisations = organisations

  if (countryCode) {
    filteredOrganisations = filteredOrganisations.filter((org) => {
      return org.target_countries.includes(countryCode)
    })
  }

  if (targetGroup) {
    filteredOrganisations = filteredOrganisations.filter((org) => {
      return org.target_groups.includes(targetGroup)
    })
  }

  if (catalogEnabledOnly) {
    filteredOrganisations = filteredOrganisations.filter((org) => {
      return org.catalog_enabled
    })
  }

  // set prioritized flag depending on context & countryCode for rendering
  filteredOrganisations = filteredOrganisations.map((org) => {
    org.prioritize = isPrioritized(org)
    return org
  })

  let ratingSummaries = ratingSummariesQuery.data

  /*
     for schueleraustausch only:
     adapt sch_de rating stats to rating api schema

     Remove this once we integrated schueleraustausch into our ratings api
   */
  if (businessArea === 'schueleraustausch') {
    ratingSummaries = ratingSummariesQuery.data.map((stats) => {
      const standardSummary = {
        UpdatedAt: stats.data.updated_at,
        organisation_id: stats.data.organisation_slug,
        count_ratings: stats.data.num_ratings,
        // NOTE: this is only present in sch_de api, but we used to render this
        // instead of _count of all ratings_
        // count_ratings_in_period: stats.data.num_in_period,
        score_total: stats.data.average,
      }

      return standardSummary
    })
  }

  // merge in rating summary information per organisation
  filteredOrganisations = filteredOrganisations.map((org) => {
    org.ratingSummary = ratingSummaries.find(({ organisation_id }) => {
      return organisation_id === org.slug
    })

    return org
  })

  // sort by count of ratings first...
  filteredOrganisations = filteredOrganisations.sort((a, b) => {
    if (!a.ratingSummary && !b.ratingSummary) {
      return 0
    }

    // b should come before a
    if (!a.ratingSummary && b.ratingSummary) {
      return -1
    }
    // a should come before b
    if (a.ratingSummary && !b.ratingSummary) {
      return 1
    }

    // compare by rating count
    return a.ratingSummary.count_ratings - b.ratingSummary.count_ratings
  }).reverse()

  // ...then sort so priority organisations are in front
  filteredOrganisations = filteredOrganisations.sort((a, b) => {
    // a is less than b by some ordering criterion
    if (!a.prioritize && b.prioritize) {
      return 1
    }
    // a is greater than b by the ordering criterion
    if (a.prioritize && !b.prioritize) {
      return -1
    }
    // a must be equal to b
    return 0
  })

  if (limit > 0 && filteredOrganisations.length > limit ) {
    filteredOrganisations = filteredOrganisations.slice(0, limit)
  }

  const handleOrderClickForOrga = (slug) => {
    const organisation = organisations.find((org) => {
      return org.slug === slug
    })

    setScrollEnabled(true)
    setSelectedOrganisation(organisation)
  }

  let renderable
  let filter = (<></>)

  if (props.userFilter) {
    filter = (
      <Filter
        businessArea={businessArea}
        countries={countries}
        targetGroups={targetGroups}
        countryCode={countryCode}
        setCountryCode={setCountryCode}
        targetGroup={targetGroup}
        setTargetGroup={setTargetGroup}
      />
    )
  }

  // console.trace(`Selected: ${selectedOrganisation}`)

  if (selectedOrganisation) {
    renderable = children({
      selectedOrganisation,
      countryCode,
      onFinish: () => {
        setSelectedOrganisation(null)
      },
    })
  } else {
    renderable = (
      <div className='organisation-list-wrapper' ref={scrolledToElement}>
        <div className="row align-items-end gy-3">
          {filter}
          <SortInfo businessArea={businessArea} />
        </div>
        <OrganisationsList
          organisations={filteredOrganisations}
          onOrderClickForOrga={handleOrderClickForOrga}
          organisationLinkPrefix={props.organisationLinkPrefix}
        />
      </div>
    )
  }

  return (
    <div className='organisation-component-root'>
      {renderable}
    </div>
  )
}

OrganisationComponentRoot.propTypes = {
  businessArea: PropTypes.string.isRequired,
  crmApiUrl: PropTypes.string.isRequired,
  crmApiToken: PropTypes.string.isRequired,
  ratingApiUrl: PropTypes.string.isRequired,
  userFilter: PropTypes.bool,
  filter: PropTypes.shape({
    countryCode: PropTypes.string,
    targetGroup: PropTypes.string,
    limit: PropTypes.string,
    catalogEnabledOnly: PropTypes.bool,
  }),
  organisationLinkPrefix: PropTypes.string,
  context: PropTypes.string,
  children: PropTypes.func,
}

export default OrganisationComponentRoot
